import pkg from "lodash";
import {constructPage, constructSort} from "./APIUtils.js";

const {isArray, isEmpty, isObject, isEqual} = pkg;


export function constructFilter(filter) {
    if (!filter) {
        return null;
    }
    if (isEmpty(filter)) {
        return null;
    }
    const filterString = "filter=" + encodeURIComponent(constructFilterString(filter));
     return filterString;
}

export function constructFilterString(filter) {
    if (isEmpty(filter)) {
        return "";
    }
    if (isArray(filter)) {
        if (filter.length === 0) {
            return "";
        } else if (filter.length === 1) {
            return constructFilterString(filter[0]);
        }
        return "(" + filter.map(f => constructFilterString(f)).join(" AND ") + ")";
    }

    if (filter.type === "AND" || filter.type === "OR") {
        const operator = filter.type;
        const filterStrings = filter.filters.map(f => constructFilterString(f))
            .filter(s => !!s && s.length > 0); // r
        return "(" + filterStrings.join(` ${operator} `) + ")";
    }

    if (!filter.type || filter.type === "condition") {
        return constructFilterCondition(filter);
    }

    return "";
}

export function constructFilterCondition(filterObject) {
    if (!validFilterObject(filterObject)) {
        console.warn("Invalid filter object in filter array! ignoring filter section: ", JSON.stringify(filterObject));

        return "";
    }

    let value = ":";

    if (typeof filterObject.value === "number") {
        // console.log("filterObject.value is a number");
        value += "'" + filterObject.value.toString() + "'";
    } else if (typeof filterObject.value === "boolean") {
        value += filterObject.value.toString();
    } else if (typeof filterObject.value === "object" && filterObject.value instanceof Date) {
        // console.log("filterObject.value is a Date");
        value += filterObject.value.toISOString(); // Convert Date to a string format like ISO 8601
    } else if (typeof filterObject.value === "string") {
        // console.log("filterObject.value is a string");
        value += `'${filterObject.value}'`;
    } else if (filterObject.value) {
        // console.log("filterObject.value is neither number, string, nor Date");
        value += filterObject.value;
    } else {
        value = "";
    }

// If you need to return the final string, you can do it here:
// return filterObject.name + ":" + filterObject.operator + value;

    return filterObject.name + ":" + filterObject.operator + value;

}

export function validFilterObject(filter) {
    if (isEmpty(filter)) {
        return false;
    }

    if (!filter.type && isArray(filter)) {
        return filter.every(validFilterObject);
    }

    if ((!filter.type && isObject(filter)) || filter.type === "condition") {
        const {name, operator, value} = filter;
        return name && operator;
    }

    if (filter.type === "AND" || filter.type === "OR") {
        if (!Array.isArray(filter.filters) || filter.filters.length === 0) {
            return false;
        }
        return filter.filters.every(validFilterObject);
    }

    return false;
}

// Example validation
export function destructFilterObject(filterString) {
    if (isEmpty(filterString)) {
        return [];
    }

    // Remove the 'filter=' prefix if present
    filterString = filterString.replace(/^filter=/, '');
    const decodedString = decodeURIComponent(filterString);
    const tokens = tokenize(decodedString);
    return parseTokens(tokens);
}

export function destructFilter(filterString) {
    if (!filterString || filterString.trim() === "") {
        return [];
    }
    const filterObject = destructFilterObject(filterString);
    if (isArray(filterObject)) {
        return filterObject;
    } else {
        if (filterObject.type === "AND") {
            return [...filterObject.filters];
        } else if (filterObject.type === "condition") {
            return [filterObject];
        } else {
            return filterObject;
        }
    }
}

// Tokenize the filter string
export function tokenize(filterString) {
    const tokens = [];
    let buffer = '';
    let inQuotes = false;

    for (let i = 0; i < filterString.length; i++) {
        const char = filterString[i];

        if (char === '(' || char === ')') {
            if (buffer.trim()) {
                tokens.push(buffer.trim());
                buffer = '';
            }
            tokens.push(char);
        } else if (char === ' ' && !inQuotes) {
            if (buffer.trim()) {
                tokens.push(buffer.trim());
                buffer = '';
            }
        } else if (char === "'" || char === '"') {
            inQuotes = !inQuotes;
            buffer += char;
        } else {
            buffer += char;
        }
    }

    if (buffer.trim()) {
        tokens.push(buffer.trim());
    }

    return tokens;
}

// Parse tokens into a filter structure
export function parseTokens(tokens) {
    const stack = [];
    let currentGroup = {type: 'AND', filters: []};

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];

        if (token === '(') {
            const newGroup = {type: 'AND', filters: []};
            stack.push(currentGroup);
            currentGroup = newGroup;
        } else if (token === ')') {
            const completedGroup = currentGroup;
            currentGroup = stack.pop();
            if (!isEmpty(completedGroup.filters)) {
                currentGroup.filters.push(completedGroup);
            }
        } else if (token === 'AND' || token === 'OR') {
            currentGroup.type = token;
        } else {
            const [name, operator, ...valueParts] = token.split(':');
            if (valueParts.length === 0) {
                currentGroup.filters.push({type: 'condition', name, operator});
                // continue;
            } else {
                const value = valueParts.join(':').replace(/^['"]|['"]$/g, ''); // remove quotes if present
                const condition = {name, operator, value};
                currentGroup.filters.push({type: 'condition', ...condition});
            }

            // If there's no type (as per the modifications), assume it's a condition
            // currentGroup.filters.push({ type: 'condition', ...condition });
        }
    }

    // If the filter group only contains one item, return it directly (array handling)
    if (currentGroup.filters.length === 1 && !stack.length) {
        return currentGroup.filters[0];
    }

    return currentGroup;
}

export function constructParameters(page, filter, sort) {
    if (isObject(page)) {
        page = constructPage(page.page, page.size);
    }
    if (isArray(filter)) {
        filter = constructFilter(filter);
    }

    if (isObject(sort)) {
        sort = constructSort(sort);
    }
    return [page, filter, sort].filter(item => !!item).join("&");
}
